/**
 * Remote cursor + viewport overlay (Plan v3-01, todo `presence`).
 *
 * Renders the absolute-positioned cursor avatars + faded viewport
 * rectangles in canvas coordinates. Plugged in inside the ReactFlow
 * `<Viewport>` so transforms (zoom / pan) apply automatically.
 */

'use client';

import { useReactFlow, useStore as useRfStore, ViewportPortal } from '@xyflow/react';
import { useEffect, useMemo } from 'react';

import { useWorkspaceItems } from '@/components/workspace/store';
import { useCanvasYjs, useOptionalCanvasYjs } from '@/components/workspace/canvas/yjs-mount';
import {
  useFollowMe,
  useHasRemotePeers,
  useParticipants,
  usePublishLocalCursor,
  usePublishLocalFocus,
  usePublishLocalScreen,
  usePublishLocalViewport,
  useRemotePresence,
} from './use-presence';
import {
  publishParticipants,
  subscribeLocateRequest,
  usePresenceSettings,
} from './presence-bridge';
import type { PresencePayload, PresenceUser } from './types';

/** Subscribe to mouse moves on the canvas root and publish them. */
export function PresenceCursorPublisher({ canvasRef }: { canvasRef: React.RefObject<HTMLElement | null> }) {
  const publish = usePublishLocalCursor();
  const rf = useReactFlow();

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const onMove = (ev: MouseEvent) => {
      const { x, y } = rf.screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
      publish(x, y);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, [canvasRef, publish, rf]);

  return null;
}

/** Publishes the local viewport rectangle whenever the ReactFlow viewport changes. */
export function PresenceViewportPublisher() {
  const publish = usePublishLocalViewport();
  // useStore's selector reads transform [x, y, zoom] and width/height
  // — together they describe the visible canvas-space rectangle.
  const tx = useRfStore((s) => s.transform[0]);
  const ty = useRfStore((s) => s.transform[1]);
  const zoom = useRfStore((s) => s.transform[2]);
  const width = useRfStore((s) => s.width);
  const height = useRfStore((s) => s.height);

  useEffect(() => {
    if (!width || !height || !zoom) return;
    publish({
      x: -tx / zoom,
      y: -ty / zoom,
      width: width / zoom,
      height: height / zoom,
    });
  }, [tx, ty, zoom, width, height, publish]);

  return null;
}

/**
 * Render layer: cursor pins + viewport rectangles for every remote
 * peer. Lives inside ReactFlow's transformed `<Viewport>` so canvas
 * pan/zoom is free.
 */
export function RemoteCursorOverlay({
  showCursors = true,
  showViewports = false,
}: {
  showCursors?: boolean;
  showViewports?: boolean;
}) {
  const peers = useRemotePresence();

  return (
    <div className="pointer-events-none absolute inset-0">
      {peers.map(({ clientId, user, payload }) => (
        <div key={clientId}>
          {showViewports && payload.viewport ? (
            <div
              className="pointer-events-none absolute rounded-md border-2 opacity-30"
              style={{
                left: payload.viewport.x,
                top: payload.viewport.y,
                width: payload.viewport.width,
                height: payload.viewport.height,
                borderColor: user.color,
              }}
              aria-label={`${user.username}'s viewport`}
            />
          ) : null}
          {showCursors && payload.cursor ? (
            <div
              className="pointer-events-none absolute -translate-x-1 -translate-y-1"
              style={{ left: payload.cursor.x, top: payload.cursor.y }}
            >
              <svg width="16" height="20" viewBox="0 0 16 20" fill={user.color}>
                <path d="M0 0 L0 16 L4 12 L7 19 L9 18 L6 11 L12 11 Z" stroke="white" strokeWidth="1" />
              </svg>
              <div
                className="absolute left-3 top-4 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium text-white shadow"
                style={{ backgroundColor: user.color }}
              >
                {user.username}
              </div>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** Publishes which app (item) the local user is focused on. */
export function PresenceFocusPublisher({
  focusedItemId,
}: {
  focusedItemId: string | null;
}) {
  const publish = usePublishLocalFocus();
  useEffect(() => {
    publish(focusedItemId);
  }, [focusedItemId, publish]);
  return null;
}

/** Publishes which screen the local camera is parked on (screens v2). */
export function PresenceScreenPublisher({
  screenItemId,
}: {
  screenItemId: string | null;
}) {
  const publish = usePublishLocalScreen();
  useEffect(() => {
    publish(screenItemId);
  }, [screenItemId, publish]);
  return null;
}

/**
 * Render layer: a colored ring + name badge around every item a remote
 * peer is focused on. Lives inside ReactFlow's transformed `<Viewport>`
 * (item rects are in canvas coordinates) so it pans/zooms with the
 * canvas. Multiple peers on the same item stack their badges.
 */
export function RemoteFocusOverlay() {
  const peers = useRemotePresence();
  const items = useWorkspaceItems();
  const byId = useMemo(
    () => new Map(items.map((it) => [it.id, it])),
    [items],
  );

  return (
    <div className="pointer-events-none absolute inset-0">
      {peers.map(({ clientId, user, payload }, i) => {
        const id = payload.focus?.itemId;
        if (!id) return null;
        const it = byId.get(id);
        if (!it) return null;
        return (
          <div
            key={clientId}
            className="pointer-events-none absolute rounded-xl"
            style={{
              left: it.state.x,
              top: it.state.y,
              width: it.state.width,
              height: it.state.height,
              boxShadow: `0 0 0 2px ${user.color}`,
            }}
            aria-hidden
          >
            <div
              className="absolute left-0 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium text-white shadow"
              // Stagger stacked badges so two peers on one app don't overlap.
              style={{ top: -22 - i * 18, backgroundColor: user.color }}
            >
              {user.username}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Canvas-side bridge (always mounted). Mirrors the participant list out
 * to the rail badge + Collaborate panel (which live OUTSIDE
 * `CanvasYjsProvider`), and executes "jump to peer" requests the panel
 * fires. Renders nothing.
 */
export function PresenceBridge() {
  const participants = useParticipants();
  const { workspaceRoom } = useCanvasYjs();
  const rf = useReactFlow();

  // Mirror participants out (membership/focus only — not cursor churn).
  useEffect(() => {
    publishParticipants(participants);
  }, [participants]);
  useEffect(() => () => publishParticipants([]), []);

  // One-shot "jump to where this peer is". Reads the peer's current
  // viewport (centre) or cursor straight from awareness at request time
  // so there's no 60Hz dependency.
  useEffect(() => {
    return subscribeLocateRequest((peerId) => {
      const awareness = workspaceRoom.awareness;
      if (!awareness) return;
      // for-of (not forEach) so TS tracks the `target` mutation across
      // the loop and narrows it correctly afterwards.
      let target: { x: number; y: number } | null = null;
      for (const stateRaw of awareness.getStates().values()) {
        const st = stateRaw as { user?: PresenceUser; presence?: PresencePayload };
        if (st.user?.peerId !== peerId) continue;
        const vp = st.presence?.viewport;
        const cur = st.presence?.cursor;
        if (vp) target = { x: vp.x + vp.width / 2, y: vp.y + vp.height / 2 };
        else if (cur) target = { x: cur.x, y: cur.y };
      }
      if (target) {
        rf.setCenter(target.x, target.y, { zoom: rf.getZoom(), duration: 350 });
      }
    });
  }, [workspaceRoom.awareness, rf]);

  return null;
}

/** The visual overlays + high-frequency publishers — only mounted once
 *  a second peer is present, and respecting the panel's view toggles. */
function PresenceOverlays({
  canvasRef,
  focusedItemId,
  screenItemId,
}: {
  canvasRef: React.RefObject<HTMLElement | null>;
  focusedItemId: string | null;
  screenItemId: string | null;
}) {
  const hasPeers = useHasRemotePeers();
  const settings = usePresenceSettings();
  if (!hasPeers) return null;
  return (
    <>
      <PresenceCursorPublisher canvasRef={canvasRef} />
      <PresenceViewportPublisher />
      <PresenceFocusPublisher focusedItemId={focusedItemId} />
      <PresenceScreenPublisher screenItemId={screenItemId} />
      <ViewportPortal>
        {settings.showCursors ? <RemoteFocusOverlay /> : null}
        <RemoteCursorOverlay
          showCursors={settings.showCursors}
          showViewports={settings.showViewports}
        />
      </ViewportPortal>
    </>
  );
}

/**
 * One-stop presence layer for the workspace canvas. Mount as a child of
 * `<ReactFlow>`. The bridge always runs (so the rail badge + panel see
 * the participant count); the cursor/viewport overlays + their 30Hz
 * broadcasts only spin up once a second peer joins.
 */
export function WorkspacePresence({
  canvasRef,
  focusedItemId,
  screenItemId,
}: {
  canvasRef: React.RefObject<HTMLElement | null>;
  focusedItemId: string | null;
  screenItemId: string | null;
}) {
  // Anon read-only canvas has no <CanvasYjsProvider> (no collab token) —
  // presence no-ops there instead of crashing the tree.
  const ctx = useOptionalCanvasYjs();
  if (!ctx) return null;
  return (
    <>
      <PresenceBridge />
      <PresenceOverlays
        canvasRef={canvasRef}
        focusedItemId={focusedItemId}
        screenItemId={screenItemId}
      />
    </>
  );
}

/**
 * Compact list of active peers (chrome). Click an avatar to follow
 * that peer's viewport.
 */
export function PresenceUserList() {
  const peers = useRemotePresence();
  const { followingPeerId, follow } = useFollowMe();

  if (peers.length === 0) return null;

  return (
    <div className="flex -space-x-2">
      {peers.map(({ clientId, user }) => {
        const active = followingPeerId === user.peerId;
        return (
          <button
            key={clientId}
            type="button"
            onClick={() => follow(active ? null : user.peerId)}
            className={`h-8 w-8 rounded-full border-2 text-xs font-semibold text-white shadow ${
              active ? 'ring-2 ring-offset-2' : ''
            }`}
            style={{ backgroundColor: user.color, borderColor: 'white' }}
            title={active ? `Following ${user.username} — click to unfollow` : `Follow ${user.username}`}
            aria-pressed={active}
          >
            {user.avatarUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img alt={user.username} src={user.avatarUrl} className="h-full w-full rounded-full object-cover" />
            ) : (
              user.username.slice(0, 1).toUpperCase()
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Snaps the local ReactFlow viewport to a followed peer's viewport.
 * Mount alongside the other publishers; releases on the next manual
 * pan/zoom (user gesture).
 */
export function FollowMeSnap() {
  const { targetViewport, follow, followingPeerId } = useFollowMe();
  const rf = useReactFlow();

  useEffect(() => {
    if (!targetViewport || !followingPeerId) return;
    // Centre the followed viewport.
    rf.setCenter(
      targetViewport.x + targetViewport.width / 2,
      targetViewport.y + targetViewport.height / 2,
      // duration:0 = jump; smoother than animate during continuous follow.
      { duration: 200 },
    );
  }, [targetViewport, followingPeerId, rf]);

  // Manual pan/zoom releases the lock — the existing canvas wheel /
  // drag handlers fire `move` events; we listen via a single window
  // listener so we don't need to thread through reactflow's prop chain.
  useEffect(() => {
    if (!followingPeerId) return;
    const release = () => follow(null);
    window.addEventListener('wheel', release, { once: true, passive: true });
    return () => window.removeEventListener('wheel', release);
  }, [followingPeerId, follow]);

  return null;
}
